<template>
  <div class="feeds">
    <div class="feeds__view-details">
      <h1>RedStone Data Feeds</h1>
      <p>
        Explore RedStone blockchain oracle database of assets with pricing from multiple sources..
      </p>
    </div>
    <Loader v-if="loading" :class="'widget-loader'" :size="40"></Loader>
    <div v-else class="feeds__actions-wrapper">
      <div style="width: 100%">
        <div class="feeds__actions-wrapper-item">
          <div class="pull-model__status mr-2">
            <span class="feeds__status-text ">
              <div>
                <i class="fa fa-globe inline"></i> Assets:
                <strong
                  v-if="Object.keys(tokensData).length > 0"
                  class="ml-2"
                  >{{ Object.keys(tokensData).length }}</strong
                >
                <vue-loaders-ball-beat
                  v-else
                  color="var(--redstone-red-color)"
                  scale="0.5"
                />
              </div>
            </span>
          </div>
          <div class="token-type-filter">
            <DropdownPicker
              :items="tokenTypeItems"
              v-model="selectedTokenTypes"
              buttonTextAll="Asset types"
              buttonTextSelected="Selected types"
              @input="handleTokenTypeChange"
              class="feeds__token-picker"
            />
          </div>
          <div
            class="feeds__actions-wrapper-label text-light fw-normal"
            style="margin-left: auto"
          >
            <span class="feeds__status-text mb-2">
              Tokens per page:
              <select
                v-model="perPage"
                @change="onPerPageChange"
                class="feeds__select"
              >
                <option
                  v-for="option in perPageOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </span>
            <span class="feeds__status-text" style="text-align: right">
              Current page:
              <select
                v-model="currentPage"
                @change="onSelectedPageChange"
                class="feeds__select"
              >
                <option
                  v-for="page in availablePages"
                  :key="page"
                  :value="page"
                >
                  {{ page }}
                </option>
              </select>
            </span>
          </div>
        </div>
        <div
          v-if="hasFiltersAndSearch"
          class="clear-filters"
          @click="resetFilters"
        >
          Clear all
        </div>

        <div class="loading-container" v-if="loading">
          <vue-loaders-ball-beat color="var(--redstone-red-color)" scale="1" />
        </div>
        <b-table
          id="tokens-table"
          ref="tokensTable"
          stacked="md"
          hover
          :items="filteredTokens"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          :filter="filters"
          :filter-function="customFilter"
          @filtered="onFiltered"
          v-if="!loading && tokensData"
          class="feeds__table"
          sort-icon-left
          sticky-header="500px"
        >
          <template #cell(name)="data">
            <img
              class="token-logo"
              :src="data.item.logoURI || logoPlaceholder"
            />
            <RouterLink
              :to="{
                name: 'TokenPage',
                params: {
                  symbol: data.item.symbol.includes('/')
                    ? data.item.symbol.replace('/', '-')
                    : data.item.symbol,
                },
              }"
            >
              <span class="token-name ml-3">{{ data.item.name }}</span>
            </RouterLink>
          </template>
          <template #cell(symbol)="data">
            <span
              class="text-truncate text-center d-block"
              :title="data.item.symbol"
            >
              {{ data.item.symbol }}
            </span>
          </template>
          <template #cell(price)="data">
            <strong
              style="font-weight: 500"
              v-if="data.item.price"
              v-html="
                parseToCurrency(
                  data.item.price,
                  denominationMap[data.item.symbol]?.denomination,
                  data.item.symbol
                )
              "
            ></strong>

            <span v-else class="feeds__no-data">no-data</span>
          </template>
          <template #cell(tags)="data">
            <div class="d-flex tag-wrapper">
              <span
                v-for="tag in data.item.tags"
                :key="tag"
                class="tag-badge mr-1"
                @click="selectTag(tag)"
              >
                {{ tag }}
              </span>
            </div>
          </template>
        </b-table>
        <b-pagination
          v-if="!loading"
          :prev-text="prevIcon"
          :next-text="nextIcon"
          limit="1"
          @change="onPageChange"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          class="my-3 custom-pagination"
        >
          <template #page="{ page }">
            <span v-if="page === currentPage" class="entry-info">
              <span v-if="totalRows > 0">
                Showing {{ displayFirstEntry }} to {{ displayLastEntry }} of
                {{ totalRows }} entries
              </span>
              <span v-else>
                No entries found
                <span v-if="hasFiltersAndSearch"> - </span>
                <span
                  style="pointer-events: all"
                  v-if="hasFiltersAndSearch"
                  class="clear-filters"
                  @click="resetFilters"
                >
                  Clear filters
                </span>
              </span>
            </span>
            <span v-else>{{ page }}</span>
          </template>
        </b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
  import redstoneAdapter from "@/redstone-api-adapter";
  import { mapActions, mapState, mapGetters } from "vuex";
  import Loader from "@/components/Loader/Loader";
  import SelectedFilters from "@/pages/Redstone/Feeds/components/SelectedFilters.vue";
  import DropdownPicker from "@/components/DropdownPicker/DropdownPicker.vue";
  import { RouteParamsHandler } from "@/core/RouteParamsHandler";
  import isScreen from "@/core/screenHelper";
  import { parseToCurrency } from "../Feeds/utils/FeedsTableDataLayer";

  const TOKEN_TYPES = [
    {
      label: "Cryptos",
      tag: "crypto",
    },
    {
      label: "LST/LRT",
      tag: "lst/lrt",
    },
    {
      label: "Currencies",
      tag: "currencies",
    },
    {
      label: "RWA",
      tag: "RWA",
    },
    {
      label: "Avalanche",
      tag: "avax",
    },
    {
      label: "ETF",
      tag: "etfs",
    },
  ];

  function simplifyPricesObject(pricesObj) {
    const simplifiedObj = {};
    for (const symbol in pricesObj) {
      simplifiedObj[symbol] = pricesObj[symbol].value;
    }
    return simplifiedObj;
  }

  export default {
    name: "TokensTable",

    components: {
      Loader,
      SelectedFilters,
      DropdownPicker,
    },

    data() {
      return {
        tokenTypes: TOKEN_TYPES,
        selectedTokenTypes: [],
        tokensData: {},
        loading: true,
        fields: [
          {
            key: "name",
            sortable: true,
            label: "Token",
            thStyle: { width: "300px" },
            sortByFormatted: true,
          },
          {
            key: "popularity",
            label: "Popularity",
            sortable: true,
            class: "d-none",
          },
          {
            key: "symbol",
            sortable: true,
            label: "Symbol",
            thStyle: { width: "150px", textAlign: "center" },
            sortByFormatted: true,
          },
          {
            key: "price",
            sortable: true,
            label: "Price",
            thStyle: { width: "150px" },
            sortByFormatted: true,
          },
          {
            key: "tags",
            sortable: true,
            label: "Asset types",
            thStyle: { width: "150px" },
          },
        ],
        logoPlaceholder:
          "https://raw.githubusercontent.com/redstone-finance/redstone-images/main/redstone-logo.png",
        currentPage: 1,
        perPage: 16,
        perPageOptions: [8, 16, 32, 64],
        filteredItems: [],
        routeParamsHandler: null,
        allTokensCountByType: {},
        isUpdatingFromQuery: false, // Flag to prevent circular updates
      };
    },

    computed: {
      ...mapGetters("manifests", ["denominationMap"]),
      ...mapState({
        searchTerm: (state) => state.layout.searchTerm,
        prices: (state) => state.prices.prices,
        pricesLoadingCompleted: (state) => state.prices.pricesLoadingCompleted,
      }),

      tokenTypeItems() {
        return this.tokenTypes.map((type) => ({
          text: `${type.label} (${this.getTypeCount(type.tag)})`,
          value: type.tag || "_all_", // Use a special value for null
          image: null, // Could add logos for token types if available
        }));
      },

      filters() {
        return {
          searchTerm: this.searchTerm,
          tokenTypes: this.selectedTokenTypes,
        };
      },

      displayedSelectedTypes() {
        if (!this.selectedTokenTypes.length) return [];

        return this.selectedTokenTypes.map((typeTag) => {
          const tokenType = this.tokenTypes.find(
            (type) => (type.tag || "_all_") === typeTag
          );
          return {
            key: typeTag,
            name: tokenType ? tokenType.label : typeTag,
          };
        });
      },

      filteredTokens() {
        return this.getFilteredTokensWithPrices(this.selectedTokenTypes);
      },

      totalRows() {
        return this.filteredItems.length || 0;
      },

      displayFirstEntry() {
        return this.totalRows === 0
          ? 0
          : (this.currentPage - 1) * this.perPage + 1;
      },

      displayLastEntry() {
        return Math.min(this.currentPage * this.perPage, this.totalRows);
      },

      availablePages() {
        const totalPages = Math.max(
          1,
          Math.ceil(this.totalRows / this.perPage)
        );
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      },

      prevIcon() {
        return isScreen("sm") || isScreen("xs") ? "Previous" : "Previous page";
      },

      nextIcon() {
        return isScreen("sm") || isScreen("xs") ? "Next" : "Next page";
      },

      hasFiltersAndSearch() {
        return (
          this.searchTerm?.trim()?.length > 0 ||
          this.selectedTokenTypes.length > 0
        );
      },

      // Added computed property to handle selectedTokenType
      selectedTokenType() {
        return this.selectedTokenTypes.length > 0;
      },
    },

    methods: {
      parseToCurrency,
      ...mapActions({
        setPricesLoadingAsCompleted: "prices/setPricesLoadingAsCompleted",
        addPrices: "prices/addPrices",
        getAllSupportedTokens: "manifests/getAllSymbolDetails",
      }),

      async lazyLoadPricesForAllTokens() {
        if (!this.pricesLoadingCompleted) {
          const prices = await redstoneAdapter.getAllPrices();
          await this.addPrices(simplifyPricesObject(prices));
          await this.setPricesLoadingAsCompleted();
        }
      },

      getFilteredTokensWithPrices(types) {
        if (!this.tokensData) return [];

        const result = [];
        const typeFilters = Array.isArray(types)
          ? types.filter((t) => t !== "_all_")
          : [];

        for (const symbol of Object.keys(this.tokensData)) {
          const token = this.tokensData[symbol];
          let shouldBeAdded = true;

          if (!token) {
            continue;
          }

          // Handle multiple token type filters
          if (typeFilters.length > 0) {
            const hasMatchingTag = typeFilters.some(
              (type) => token.tags && token.tags.includes(type)
            );

            if (!hasMatchingTag) {
              shouldBeAdded = false;
            }
          }

          const searchTerm = this.searchTerm || "";
          const searchTermLowerCase = searchTerm.toLowerCase();
          if (searchTerm) {
            const nameIncludesSearchTerm = (token.name || "")
              .toLowerCase()
              .includes(searchTermLowerCase);
            const symbolIncludesSearchTerm = (symbol || "")
              .toLowerCase()
              .includes(searchTermLowerCase);
            const customCommentIncludesSearchTerm =
              token?.comment &&
              token.comment.toLowerCase().includes(searchTermLowerCase);
            if (
              !nameIncludesSearchTerm &&
              !symbolIncludesSearchTerm &&
              !customCommentIncludesSearchTerm
            ) {
              shouldBeAdded = false;
            }
          }

          if (shouldBeAdded) {
            result.push({
              symbol: symbol,
              ...token,
              price: this.prices[symbol],
            });
          }
        }
        return result;
      },

      calculateTokenTypeCount() {
        if (!this.tokensData) return;

        // Calculate count of all tokens
        const allTokens = Object.values(this.tokensData).filter(
          (token) => token
        );
        this.allTokensCountByType = {
          _all_: allTokens.length,
        };

        // Calculate count for each token type
        this.tokenTypes.forEach((type) => {
          if (type.tag) {
            const count = allTokens.filter(
              (token) => token.tags && token.tags.includes(type.tag)
            ).length;
            this.allTokensCountByType[type.tag] = count;
          }
        });
      },

      getTypeCount(type) {
        if (!type) return this.allTokensCountByType["_all_"] || 0;
        return this.allTokensCountByType[type] || 0;
      },

      selectTag(tag) {
        const matchingType = this.tokenTypes.find((type) => type.tag === tag);
        if (matchingType) {
          // If already selected, do nothing
          if (this.selectedTokenTypes.includes(tag)) return;

          // Otherwise add to selected types
          this.selectedTokenTypes = [...this.selectedTokenTypes, tag];
          this.handleTokenTypeChange();
        }
      },

      handleTokenTypeChange() {
        this.currentPage = 1;
        this.$nextTick(() => {
          this.applyFilters();
          this.updateRouteParams();
        });
      },

      removeTokenType(item) {
        this.selectedTokenTypes = this.selectedTokenTypes.filter(
          (type) => type !== item.key
        );
        this.currentPage = 1;
        this.$nextTick(() => {
          this.applyFilters();
          this.updateRouteParams();
        });
      },

      resetFilters(clearSearch = true) {
        if (clearSearch) {
          this.$store.dispatch("layout/updateSearchTerm", "");
        }
        this.selectedTokenTypes = [];
        this.currentPage = 1;

        this.$router
          .replace({
            query: {},
          })
          .catch(() => {});

        this.$nextTick(() => {
          this.$store.dispatch("layout/updateFeedsFilterStatus", false);
          this.applyFilters();

          if (this.routeParamsHandler) {
            this.routeParamsHandler.updateRouteParams({});
          }
        });
      },

      applyFilters() {
        if (this.$refs.tokensTable) {
          this.$refs.tokensTable.refresh();
        }
      },

      onFiltered(filteredItems) {
        this.filteredItems = filteredItems;
        const maxPage = Math.ceil(filteredItems.length / this.perPage) || 1;
        if (this.currentPage > maxPage) {
          this.currentPage = maxPage;
          this.$nextTick(() => {
            this.updateRouteParams();
          });
        }
      },

      onPerPageChange() {
        const newMaxPage = Math.ceil(this.totalRows / this.perPage);
        if (this.currentPage > newMaxPage) {
          this.currentPage = newMaxPage || 1;
        }
        this.$nextTick(() => {
          this.updateRouteParams();
          this.applyFilters();
        });
      },

      onSelectedPageChange() {
        this.$nextTick(() => {
          this.updateRouteParams();
          this.applyFilters();
        });
      },

      onPageChange(page) {
        this.currentPage = page;
        this.$nextTick(() => {
          this.updateRouteParams();
          this.applyFilters();
        });
      },

      customFilter(row, filters) {
        const matchesSearch =
          !filters.searchTerm?.length ||
          row.name?.toLowerCase().includes(filters.searchTerm?.toLowerCase()) ||
          row.symbol
            ?.toLowerCase()
            .includes(filters.searchTerm?.toLowerCase()) ||
          (row.comment &&
            row.comment
              .toLowerCase()
              .includes(filters.searchTerm?.toLowerCase()));

        const matchesTokenType =
          !filters.tokenTypes?.length ||
          filters.tokenTypes.includes("_all_") ||
          (row.tags &&
            filters.tokenTypes.some((type) => row.tags.includes(type)));

        return matchesSearch && matchesTokenType;
      },

      // FIXED: Enhanced updateRouteParams to ensure it works correctly with the router
      updateRouteParams() {
        if (!this.routeParamsHandler) return;

        const params = {
          currentPage: this.currentPage,
          perPage: this.perPage,
          searchTerm: this.searchTerm,
          tokenTypes: this.selectedTokenTypes,
        };

        Object.keys(params).forEach((key) => {
          if (Array.isArray(params[key]) && params[key].length === 0) {
            delete params[key];
          } else if (
            params[key] === undefined ||
            params[key] === "" ||
            params[key] === null
          ) {
            delete params[key];
          }
        });

        this.routeParamsHandler.updateRouteParams(params);

        // As a backup, also directly update the URL if needed
        const query = {};
        if (params.searchTerm) query.searchTerm = params.searchTerm;
        if (params.tokenTypes && params.tokenTypes.length) {
          query.tokenTypes = params.tokenTypes.join(",");
        }
        if (params.currentPage && params.currentPage > 1) {
          query.page = params.currentPage;
        }
        if (params.perPage && params.perPage !== this.perPageOptions[1]) {
          query.perPage = params.perPage;
        }

        // Only update if different from current query to avoid navigation loops
        const currentQuery = this.$route.query;
        const hasChanged =
          JSON.stringify(query) !== JSON.stringify(currentQuery);

        if (hasChanged && !this.isUpdatingFromQuery) {
          this.$router
            .replace({
              query,
            })
            .catch(() => {
              // Ignore navigation errors
            });
        }
      },

      async initializeFromRoute() {
        const routeParams =
          this.routeParamsHandler.initializeFiltersFromRoute();
        this.perPage = routeParams.perPage || this.perPageOptions[1];

        // Handle tokenTypes properly whether search is active or not
        if (routeParams.tokenTypes && routeParams.tokenTypes.length > 0) {
          this.selectedTokenTypes = routeParams.tokenTypes;
        }

        if (routeParams.searchTerm) {
          await this.$store.dispatch(
            "layout/updateSearchTerm",
            routeParams.searchTerm
          );
        }

        await this.$nextTick();
        this.applyFilters();

        // Set current page after filters are applied to get correct max page
        const maxPage =
          Math.ceil(this.filteredItems.length / this.perPage) || 1;
        this.currentPage = Math.min(routeParams.currentPage || 1, maxPage);
      },

      // Get searchTerm from URL query parameters
      getSearchTermFromQuery() {
        if (this.isUpdatingFromQuery) return;

        this.isUpdatingFromQuery = true;

        if (this.$route.query.searchTerm) {
          const querySearchTerm = this.$route.query.searchTerm;
          // Only update if different from current searchTerm to avoid loops
          if (querySearchTerm !== this.searchTerm) {
            this.$store.dispatch("layout/updateSearchTerm", querySearchTerm);
          }
        }

        // Handle token types from query
        if (this.$route.query.tokenTypes) {
          const queryTokenTypes = this.$route.query.tokenTypes.split(",");
          if (
            JSON.stringify(queryTokenTypes) !==
            JSON.stringify(this.selectedTokenTypes)
          ) {
            this.selectedTokenTypes = queryTokenTypes;
            this.$nextTick(() => {
              this.applyFilters();
            });
          }
        }

        this.isUpdatingFromQuery = false;
      },
    },

    async created() {
      try {
        this.tokensData = await this.getAllSupportedTokens();
        this.loading = false;
        this.calculateTokenTypeCount();
      } catch (error) {
        console.error("Error loading tokens:", error);
      }
    },

    async mounted() {
      this.routeParamsHandler = new RouteParamsHandler(this.$router, {
        tokenTypesParam: "tokenTypes", // Ensure we use the correct parameter name
        searchParam: "searchTerm", // Use searchTerm as the parameter name
      });

      await this.lazyLoadPricesForAllTokens();
      await this.$nextTick();
      this.filteredItems = this.getFilteredTokensWithPrices(
        this.selectedTokenTypes
      );

      // Get search term and token types from query parameters if present
      this.getSearchTermFromQuery();

      await this.initializeFromRoute();
      this.routeParamsHandler.setInitialLoadComplete();
    },

    watch: {
      searchTerm: {
        async handler(newValue) {
          if (this.isUpdatingFromQuery) return;

          this.currentPage = 1;
          await this.$nextTick();
          this.applyFilters();
          this.updateRouteParams();

          if (newValue === "") {
            await this.$store.dispatch("layout/updateFeedsFilterStatus", true);
          } else if (newValue?.length >= 3) {
            await this.$store.dispatch("layout/updateFeedsFilterStatus", false);
          }
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "~@/styles/app";

  .token-logo {
    width: 40px;
    height: 40px;
    padding: 5px;
    border-radius: 40px;
    margin: 10px;
  }

  .loading-container {
    height: 100px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .token-name {
    font-size: 14px;
  }

  .tag-badge {
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    display: inline-block;
    margin-top: 4px;
    cursor: pointer;
  }

  .tag-wrapper {
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }

  .token-type-filter {
    margin-right: 15px;

    ::v-deep .multiselect-dropdown-container {
      display: inline-block;
      width: 200px;
      vertical-align: middle;
    }
  }

  .selected-items {
    font-size: 12px;
    margin-bottom: 5px;
  }

  .clear-filters {
    color: var(--redstone-red-color);
    cursor: pointer;
    font-size: 14px;
    margin-top: 10px;
    margin-bottom: 10px;
    display: inline-block;
  }

  .feeds {
    background: #fff;
    width: 100%;
    margin-bottom: 20px;

    &__view-details {
      h1 {
        font-size: 18px;
        margin-bottom: 10px;
      }

      p {
        margin-bottom: 0;
      }
    }

    &__actions-wrapper {
      border-radius: 15px;
      background-color: #fff;
      margin-bottom: 20px;

      &-item {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }

      &-label {
        font-size: 14px;
        display: flex;
        flex-direction: column;
      }
    }

    &__status {
      display: flex;
      align-items: center;

      @media (max-width: 768px) {
        width: 100%;
        margin: 10px 0;
      }
    }

    &__status-text {
      margin-right: 15px;
      color: rgb(94, 94, 94);
      font-size: 14px;

      strong {
        color: rgb(174, 8, 34);
      }
    }

    &__select {
      border-radius: 4px;
      padding: 4px 8px;
      margin-left: 5px;
      font-size: 14px;
      color: $navy;
    }

    &__token-picker {
      min-width: 200px;
    }

    &__table {
      table-layout: fixed;
      margin-bottom: 1rem;

      th {
        text-transform: uppercase;
        color: rgba(0, 0, 0, 0.6);
        font-size: 12px;
        background-position: 30px !important;
        padding: 10px 15px !important;
        text-align: center;
        border: 1px solid rgb(227, 227, 227) !important;
        letter-spacing: 0.5px;
        background-color: #f4f4f4;

        &:first-child {
          text-align: left;
        }
      }

      thead tr {
        background: #f4f4f4;
      }

      tbody tr {
        &:hover {
          background: #f8f8f8 !important;
        }

        &:nth-child(even) {
          background: #f4f4f4 !important;
        }

        td {
          text-align: center;
          border-top: 1px solid rgb(227, 227, 227);
          border-bottom: 1px solid rgb(227, 227, 227);
          padding: 15px 10px;

          &:first-child {
            text-align: left;
          }

          @media (min-width: breakpoint-min(lg)) {
            &:first-child {
              border-left: 1px solid rgb(227, 227, 227);
            }

            &:last-child {
              border-right: 1px solid rgb(227, 227, 227);
            }
          }
        }
      }

      @media (max-width: breakpoint-min(md)) {
        border: 0;

        thead {
          display: none;
        }

        tbody tr {
          border: 1px solid #ced4da;
          margin-bottom: 1rem;
          display: block;
          border-radius: 5px;
          height: auto;

          &:nth-child(even) {
            background-color: #fff !important;
          }

          td {
            display: block;
            text-align: left;
            padding: 15px 0 !important;
            position: relative;
            border: 0 !important;

            div {
              width: 100% !important;
            }

            &::before {
              content: attr(data-label);
              text-align: left;
              font-size: 10px;
            }
          }
        }
      }
    }
  }

  .selected-filters-icons {
    display: flex;
    flex-wrap: wrap;
  }

  .custom-pagination {
    .entry-info {
      font-size: 14px;
    }
  }

  .table-loader {
    height: 100px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .separator {
    width: 1px;
    height: 30px;
    margin: 0 15px;
  }
</style>
